import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { applyDisplayFilterFields } from "../../../../actions/Tenants/config/applyDisplayFilter";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import MetaForm, { metaFormIsValid } from "../../../Forms/MetaForm";
import { TenantItemModal } from "./TenantItemModal";
import { getTableHead, Table, TableCell, TableHeaderCell } from "../../../Helper/Table";
import { Button } from "../../../Helper/Button";
import { useFields, usePagination } from "../../../../hooks";
import { formatDateTime } from "../../Layers/fields/formatters";

const endpoint = "/tenant-item/";

const TenantItemsPage = () => {
    const config = useConfig();
    const meta_form = applyDisplayFilterFields(config.root_config?.tenant_items_meta_form);
    const [state, setState] = useState<{ [key: string]: any }>({});
    const [error, setError] = useState<string[]>([]);
    const [isModalOpen, setModalOpen] = useState(false);

    const queryClient = useQueryClient();

    const { offset, limit } = usePagination();

    useEffect(() => {
        if (state && Object.keys(state).length > 0) {
            setModalOpen(true);
        }
    }, [state]);

    const closeModal = () => {
        setModalOpen(false);

        setTimeout(() => {
            setState({});
        }, 300);
    };

    const getItems = async (offset, limit) => {
        const res = await callAvosApi(`/tenants/${config.tenant_id}${endpoint}?offset=${offset}&limit=${limit}`);
        return res.data;
    };

    const { data: tenantItems, isLoading } = useQuery({
        queryKey: ["tenantItems", offset, limit],
        queryFn: () => getItems(offset, limit),
    });

    const { mutate: saveTenantItem } = useMutation({
        mutationFn: async (tenantItem: any) => {
            const { external_id, id, active, ...data } = tenantItem;
            return callAvosApi(`/tenants/${config.tenant_id}${endpoint}${id ?? ""}`, {
                method: id ? "PUT" : "POST",
                data: { config: data, active, external_id },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tenantItems"], exact: false });
            closeModal();
        },
    });

    const onDeleteItem = async (id: string) => {
        saveTenantItem({ id, active: false });
    };

    const setValue = (field, value) => setState({ ...state, [field]: value });

    const onSaveTenantItem = () => {
        const invalidFields = Object.keys(state).filter((field) => {
            const fieldConfig = meta_form.find((i) => i.name === field);
            return !metaFormIsValid([fieldConfig], { [field]: state[field] });
        });

        setError(invalidFields);
        if (invalidFields.length > 0) {
            return;
        }

        saveTenantItem(state);
    };

    const { applyDisplayFilterLayerFields } = useFields();
    const fields = applyDisplayFilterLayerFields(config.root_config.items_table_fields || [], {});

    const header = useMemo(() => (
        <tr className="position-relative">
            {getTableHead(fields)}

            <TableHeaderCell>Created</TableHeaderCell>
            <TableHeaderCell sticky />
        </tr>
    ), [fields.length]);

    return (
        <div className="d-flex flex-column">
            <TenantItemModal
                isOpen={isModalOpen}
                onClose={closeModal}
                title={state?.id ? "Edit item" : "Create item"}
                onConfirm={onSaveTenantItem}
                onDelete={(state as any)?.id ? () => onDeleteItem((state as any).id!) : undefined}
                entityName={"item"}
            >
                <MetaForm
                    meta={meta_form}
                    object={state}
                    config={config}
                    setValue={setValue}
                />

                {error.length > 0 && (
                    <div>
                        <h4>Validation Errors:</h4>
                        <ul>
                            {error.map((err, index) => (
                                <li key={index}>{err}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </TenantItemModal>

            <Button icon={faPlus} color="primary" onClick={() => setModalOpen(true)} className="d-block ms-auto mb-3">
                Add Item
            </Button>

            <Table
                withWrapper
                count={tenantItems?.count || 0}
                header={header}
                isLoading={isLoading}
                emptyStateText="No items available"
            >
                <tbody>
                    {tenantItems?.results?.map((item, index) => (
                        <tr key={index} title={item.id} className="position-relative">
                            {fields.map((field, fieldIndex) => (
                                <TableCell key={fieldIndex}>
                                    {field.set_value({
                                        config,
                                        layer: { ...item, ...item.config },
                                        check: null, // * To be used in check tables and layer overview
                                        fruit: null, // * Fruit list is not available on overview screen
                                        children: null, // * children is only available on layer index page
                                        form_options: [] // * used to translate form values to labels
                                    }).display_component()}
                                </TableCell>
                            ))}

                            <TableCell>{formatDateTime(item.created)}</TableCell>

                            <TableCell sticky>
                                <Button
                                    key={index}
                                    size="sm"
                                    className="m-1"
                                    color="light"
                                    onClick={() => setState({ ...item.config, id: item.id, external_id: item.external_id })}
                                >
                                    Edit
                                </Button>
                            </TableCell>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

export default TenantItemsPage;
